import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, ButtonGroup, Typography } from '@mui/material'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { usePatternContext } from '../contexts/PatternContext'
import useSaveCustomIndicator from '../../../hooks/useSaveCustomIndicator'
import { COMPOSITE_LIMITS, CompositeOutput, CustomIndicatorDoc } from '../../../types/composite'
import {
  CompositeValidationIssue,
  inferCompositeScale,
  isNodeRef,
  nodeRefs,
  validateComposite,
} from '../../../common/utils/composite'
import StepCard from './StepCard'
import OutputsEditor from './OutputsEditor'
import { DraftStep, draftToNodes, newIndicatorStep, newMathStep, nodesToDraft } from './types'

/**
 * Everything the surrounding detail-view header needs from the editor. The
 * editor stays the single owner of the draft state; the header is pure
 * presentation on top of this api.
 */
export interface CompositeEditorRenderApi {
  name: string
  setName: (name: string) => void
  maxNameLength: number
  publish: () => void
  publishLabel: string
  publishDisabled: boolean
  saving: boolean
  /** True when the draft differs from the loaded doc (or is a non-empty new
   * draft) — closing should confirm before discarding. */
  dirty: boolean
  scale: 'overlay' | 'oscillator'
  setScale: (scale: 'overlay' | 'oscillator') => void
  /** Draft state exposure for the AI copilot host (reads and whole-draft applies). */
  steps: DraftStep[]
  setSteps: (steps: DraftStep[]) => void
  outputs: CompositeOutput[]
  setOutputs: (outputs: CompositeOutput[]) => void
  /** The steps/outputs/validation editor, rendered header-free. */
  body: React.ReactNode
}

interface CompositeEditorProps {
  editDoc?: CustomIndicatorDoc
  children: (api: CompositeEditorRenderApi) => React.ReactElement
}

const CompositeEditor: React.FC<CompositeEditorProps> = ({ editDoc, children }) => {
  const { usermeta, tradingHouse } = useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const { getIndicatorByValue } = usePatternContext()
  const { publish } = useSaveCustomIndicator({ gcid: usermeta?.gcid, tradingHouse })

  const [name, setName] = useState(editDoc?.name ?? '')
  // New indicators start EMPTY (pattern-editor parity: empty state + CTA,
  // not a preconfigured draft)
  const [steps, setSteps] = useState<DraftStep[]>(() =>
    editDoc ? nodesToDraft(editDoc.def.nodes) : []
  )
  const [outputs, setOutputs] = useState<CompositeOutput[]>(() =>
    editDoc ? editDoc.def.outputs.map((o) => ({ ...o, ref: { ...o.ref } })) : []
  )
  const [saving, setSaving] = useState(false)
  const [scale, setScaleState] = useState<'overlay' | 'oscillator'>(
    editDoc?.def.scale ?? 'oscillator'
  )
  const scaleTouchedRef = useRef(!!editDoc)

  const setScale = (next: 'overlay' | 'oscillator') => {
    scaleTouchedRef.current = true
    setScaleState(next)
  }

  // Until the author chooses, track the inferred scale as the default
  useEffect(() => {
    if (scaleTouchedRef.current) return
    setScaleState(inferCompositeScale(draftToNodes(steps), outputs, getIndicatorByValue))
  }, [steps, outputs, getIndicatorByValue])

  const validation = useMemo(
    () => validateComposite(draftToNodes(steps), outputs, getIndicatorByValue),
    [steps, outputs, getIndicatorByValue]
  )

  // Publish is explicit (no autosave), so Back must know whether it discards
  const dirty = useMemo(() => {
    if (!editDoc) return steps.length > 0 || outputs.length > 0 || !!name.trim()
    const current = JSON.stringify({
      name,
      nodes: draftToNodes(steps),
      outputs,
      scale,
    })
    const baseline = JSON.stringify({
      name: editDoc.name,
      nodes: editDoc.def.nodes,
      outputs: editDoc.def.outputs,
      scale: editDoc.def.scale ?? 'oscillator',
    })
    return current !== baseline
  }, [editDoc, name, steps, outputs, scale])

  // Issues render at their source — on the offending step card or output row
  // (pattern-builder-style red glow). Only issues with no anchor (e.g. "Expose
  // at least one output") fall through to the editor-level list.
  const { issuesByStep, issuesByOutput, globalIssues } = useMemo(() => {
    const byStep = new Map<string, CompositeValidationIssue[]>()
    const byOutput = new Map<number, CompositeValidationIssue[]>()
    const global: CompositeValidationIssue[] = []
    const stepIds = new Set(steps.map((s) => s.id))
    for (const issue of validation.issues) {
      if (issue.nodeId && stepIds.has(issue.nodeId)) {
        byStep.set(issue.nodeId, [...(byStep.get(issue.nodeId) ?? []), issue])
      } else if (issue.outputIndex !== undefined && issue.outputIndex < outputs.length) {
        byOutput.set(issue.outputIndex, [...(byOutput.get(issue.outputIndex) ?? []), issue])
      } else {
        global.push(issue)
      }
    }
    return { issuesByStep: byStep, issuesByOutput: byOutput, globalIssues: global }
  }, [validation.issues, steps, outputs.length])

  // A step is deletable only while nothing later (or an output) references it.
  const deleteDisabledReason = (step: DraftStep, index: number): string | undefined => {
    const nodes = draftToNodes(steps)
    for (let i = index + 1; i < nodes.length; i++) {
      if (nodeRefs(nodes[i]).some((ref) => isNodeRef(ref) && ref.node === step.id)) {
        return `Referenced by step ${i + 1}`
      }
    }
    if (outputs.some((o) => o.ref.node === step.id)) return 'Referenced by an output'
    return undefined
  }

  // Freshly added steps open expanded (nothing configured yet); loaded steps
  // start collapsed like saved conditions
  const [lastAddedStepId, setLastAddedStepId] = useState<string | null>(null)

  const addStep = (factory: () => DraftStep) => {
    const step = factory()
    setSteps((prev) => [...prev, step])
    setLastAddedStepId(step.id)
    if (outputs.length === 0) {
      setOutputs([{ field: 'value1', ref: { node: step.id } }])
    }
  }

  const moveStep = useCallback((from: number, to: number) => {
    setSteps((prev) => {
      if (from === to || from < 0 || from >= prev.length || to < 0 || to >= prev.length) {
        return prev
      }
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }, [])

  const handlePublish = () => {
    setSaving(true)
    publish({ id: editDoc?.id, name, nodes: draftToNodes(steps), outputs, scale })
      .then((saved) => {
        setSnackbarSettings({
          severity: 'success',
          message: `${saved.name} v${saved.latestVersion} published`,
        })
        // Stay in the editor, rebaselined on the CANONICAL def: publishing
        // renumbers node ids (n1..nk), so keeping uuid/stale-id steps would
        // leave dirty stuck true forever (spurious Back prompt, no-op
        // republishes). The live listener refreshes editDoc to match.
        setSteps(nodesToDraft(saved.def.nodes))
        setOutputs(saved.def.outputs)
        setName(saved.name)
      })
      .catch((error: any) => {
        setSnackbarSettings({
          severity: 'error',
          message: error?.message ?? 'Failed to publish indicator',
        })
      })
      .finally(() => setSaving(false))
  }

  const addStepButtons = (
    <ButtonGroup
      size="small"
      variant="outlined"
      disabled={steps.length >= COMPOSITE_LIMITS.maxNodes}
    >
      <Button onClick={() => addStep(newIndicatorStep)}>+ Indicator</Button>
      <Button onClick={() => addStep(newMathStep)}>+ Math</Button>
    </ButtonGroup>
  )

  // Empty state mirrors PatternDetailView's "no condition groups" treatment
  const body =
    steps.length === 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          gap: 2,
        }}
      >
        <ShowChartIcon sx={{ fontSize: 48, opacity: 0.15 }} />
        <Typography variant="body2" color="text.secondary">
          No steps defined. Chain indicators and math over chart data into new values.
        </Typography>
        {addStepButtons}
      </Box>
    ) : (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Steps */}
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Box>
            <Typography variant="subtitle1">Steps</Typography>
            <Typography variant="body2" color="text.secondary">
              Chain indicators and math — steps can use chart data directly and reference earlier
              steps.
            </Typography>
          </Box>

          {steps.map((step, index) => (
            <StepCard
              key={step.id}
              step={step}
              index={index}
              steps={steps}
              onChange={(next) => setSteps((prev) => prev.map((s, i) => (i === index ? next : s)))}
              onDelete={() => setSteps((prev) => prev.filter((_, i) => i !== index))}
              deleteDisabledReason={deleteDisabledReason(step, index)}
              getIndicatorByValue={getIndicatorByValue}
              issues={issuesByStep.get(step.id)}
              onMove={moveStep}
              defaultExpanded={step.id === lastAddedStepId}
            />
          ))}

          <Box display="flex" alignItems="center" gap={1.5}>
            {addStepButtons}
            <Typography variant="caption" color="text.secondary">
              {steps.length} / {COMPOSITE_LIMITS.maxNodes} steps
            </Typography>
          </Box>
        </Box>

        {/* Outputs */}
        <OutputsEditor
          outputs={outputs}
          onChange={setOutputs}
          steps={steps}
          getIndicatorByValue={getIndicatorByValue}
          issuesByOutput={issuesByOutput}
        />

        {/* Editor-level validation — anchored issues render on their step/output */}
        {globalIssues.length > 0 && (
          <Box display="flex" flexDirection="column" gap={0.5}>
            {globalIssues.map((issue, index) => (
              <Alert key={index} severity={issue.level} sx={{ py: 0, alignItems: 'center' }}>
                {issue.message}
              </Alert>
            ))}
          </Box>
        )}

        {validation.valid && (
          <Typography variant="caption" color="text.secondary">
            Needs ~{validation.minCandles} candles of history before values appear
          </Typography>
        )}
      </Box>
    )

  return children({
    name,
    setName,
    maxNameLength: COMPOSITE_LIMITS.maxNameLength,
    publish: handlePublish,
    publishLabel: editDoc ? `Publish v${editDoc.latestVersion + 1}` : 'Publish',
    // Existing docs republish only when something changed — right after
    // create-on-entry an immediate publish would just duplicate v1 into v2
    publishDisabled: !validation.valid || !name.trim() || saving || (!!editDoc && !dirty),
    saving,
    dirty,
    scale,
    setScale,
    steps,
    setSteps,
    outputs,
    setOutputs,
    body,
  })
}

export default CompositeEditor
